import Alert from "@cloudscape-design/components/alert"
import Autosuggest from "@cloudscape-design/components/autosuggest"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table from "@cloudscape-design/components/table"
import { useState } from "react"
import type { TestConceptGroup } from "@/generated/sdk"
import CollapsibleRule from "@/components/RunDetailModal/CollapsibleRule"
import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { isAutoRepaired, noRuleReason, testConceptHasNoValueColumn } from "./validation"

interface Props {
  item: TestConceptGroup
  index: number
  onChange: (index: number, updated: TestConceptGroup) => void
  onRemove: (index: number) => void
  canEdit: boolean
  columnNames: string[]
  showErrors: boolean
}

interface TableRow {
  rowIndex: number
  row: Record<string, string>
}

export function TestConceptEditor({
  item,
  index,
  onChange,
  onRemove,
  canEdit,
  columnNames,
  showErrors,
}: Props) {
  const [tableOpen, setTableOpen] = useState(false)
  const [ruleRow, setRuleRow] = useState<number | null>(null)
  const update = (patch: Partial<TestConceptGroup>) => onChange(index, { ...item, ...patch })
  const instrumentColumns = item.instrument_column ?? []
  const valueColumnsError = showErrors && testConceptHasNoValueColumn(item)
  const allCols = [
    ...new Set([...instrumentColumns, ...item.conditionColumns, ...item.valueColumns]),
  ]

  const addConditionCol = (col: string) => {
    if (!col || item.conditionColumns.includes(col)) return
    const updated = { conditionColumns: [...item.conditionColumns, col] }
    // Add column to existing rows if not present
    const rows = item.rows.map((r) => (col in r ? r : { ...r, [col]: "" }))
    update({ ...updated, rows })
  }

  const addValueCol = (col: string) => {
    if (!col || item.valueColumns.includes(col)) return
    const updated = { valueColumns: [...item.valueColumns, col] }
    const rows = item.rows.map((r) => (col in r ? r : { ...r, [col]: "" }))
    update({ ...updated, rows })
  }

  const removeCol = (col: string) => {
    update({
      conditionColumns: item.conditionColumns.filter((c) => c !== col),
      valueColumns: item.valueColumns.filter((c) => c !== col),
      rows: item.rows.map((r) => {
        const next = { ...r }
        delete next[col]
        return next
      }),
    })
  }

  const changeCol = (oldCol: string, newCol: string) => {
    if (!newCol || newCol === oldCol || allCols.includes(newCol)) return
    update({
      conditionColumns: item.conditionColumns.map((c) => (c === oldCol ? newCol : c)),
      valueColumns: item.valueColumns.map((c) => (c === oldCol ? newCol : c)),
      rows: item.rows.map((r) => {
        const next = { ...r }
        next[newCol] = next[oldCol] ?? ""
        delete next[oldCol]
        return next
      }),
    })
  }

  const updateCell = (rowIdx: number, col: string, val: string) => {
    const rows = [...item.rows]
    rows[rowIdx] = { ...rows[rowIdx], [col]: val }
    update({ rows })
  }

  const addRow = () => {
    const empty: Record<string, string> = {}
    for (const col of allCols) empty[col] = ""
    for (const col of instrumentColumns) empty[col] = item.instrument ?? ""
    update({ rows: [...item.rows, empty] })
  }

  const removeRow = (rowIdx: number) => {
    update({ rows: item.rows.filter((_, i) => i !== rowIdx) })
  }

  const availableCols = columnNames.filter((c) => !allCols.includes(c))

  const columnSummary = `Conditions: ${item.conditionColumns.join(", ") || "none"} · Values: ${item.valueColumns.join(", ")}`
  const summary =
    item.name || item.instrument
      ? [item.name, item.instrument, columnSummary].filter(Boolean).join(" · ")
      : item.conditionColumns.length > 0 || item.valueColumns.length > 0
        ? columnSummary
        : "New group"
  const autoRepaired = isAutoRepaired(item)
  // What enabling would leave unvalidated, so the review alert never promises
  // a rule the generator will not emit.
  const reasonIfEnabled = noRuleReason({ ...item, enabled: true })

  // Enabling an auto-repaired group is the DAE's review sign-off, so it stops
  // being auto-repaired: drop the marker name and treat it as a normal group.
  const setEnabled = (enabled: boolean) => {
    const updated: TestConceptGroup = { ...item, enabled }
    if (enabled && autoRepaired) delete updated.name
    onChange(index, updated)
  }

  const renderColumnPill = (col: string) =>
    canEdit ? (
      <SpaceBetween key={col} direction="horizontal" size="xxs">
        <Select
          ariaLabel={`Rename column ${col}`}
          selectedOption={{ value: col, label: col }}
          options={[
            { value: col, label: col },
            ...availableCols.map((c) => ({ value: c, label: c })),
          ]}
          onChange={({ detail }) => changeCol(col, detail.selectedOption.value ?? col)}
        />
        <Button iconName="close" variant="icon" ariaLabel="Remove" onClick={() => removeCol(col)} />
      </SpaceBetween>
    ) : (
      <Box key={col} display="inline-block">
        {col}
      </Box>
    )

  const tableItems: TableRow[] = item.rows.map((row, i) => ({ rowIndex: i, row }))

  const columnCell = (col: string) => (tr: TableRow) =>
    canEdit ? (
      <Input
        value={tr.row[col] || ""}
        ariaLabel={`${col} row ${tr.rowIndex + 1}`}
        onChange={({ detail }) => updateCell(tr.rowIndex, col, detail.value)}
      />
    ) : (
      <ReadOnlyValue value={tr.row[col] || ""} />
    )

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText={
        <span>
          {summary}
          {valueColumnsError && (
            <Box variant="span" color="text-status-error" fontSize="body-s">
              {" "}
              (incomplete)
            </Box>
          )}
        </span>
      }
      headerCounter={`${item.rows.length} rows`}
      headerActions={
        canEdit ? (
          <Checkbox
            checked={item.enabled !== false}
            onChange={({ detail }) => setEnabled(detail.checked)}
          >
            Enabled
          </Checkbox>
        ) : undefined
      }
    >
      <SpaceBetween size="l">
        {autoRepaired && (
          <Alert type="info" header="Review before enabling">
            {canEdit
              ? "Extraction found this table without a value column, so it chose one and disabled the test concept. Check the condition and value columns before enabling it."
              : "Extraction found this table without a value column, so it chose one and disabled the test concept for review."}{" "}
            {reasonIfEnabled
              ? `Even if enabled, it generates no data quality rule because ${reasonIfEnabled}.`
              : "It generates no data quality rule until it is enabled."}
          </Alert>
        )}
        {/* Column roles */}
        <SpaceBetween size="m">
          {item.instrument && (
            <SpaceBetween size="xxs">
              <Box variant="awsui-key-label">Instrument</Box>
              <ReadOnlyValue value={item.instrument} />
            </SpaceBetween>
          )}

          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Condition Columns</Box>
            <SpaceBetween size="xxs" direction="horizontal">
              {item.conditionColumns.map(renderColumnPill)}
            </SpaceBetween>
            {canEdit && (
              <ColPicker available={availableCols} onPick={addConditionCol} label="+ Add Column" />
            )}
          </SpaceBetween>

          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Value Columns</Box>
            <FormField
              errorText={
                valueColumnsError
                  ? "Select at least one value column. This is the column that holds the result each row records (not the same as filling in cell values)."
                  : undefined
              }
            >
              <SpaceBetween size="xxs" direction="horizontal">
                {item.valueColumns.map(renderColumnPill)}
              </SpaceBetween>
            </FormField>
            {canEdit && (
              <ColPicker available={availableCols} onPick={addValueCol} label="+ Add Column" />
            )}
          </SpaceBetween>
        </SpaceBetween>

        {/* Data table - collapsible */}
        {allCols.length > 0 && (
          <SpaceBetween size="xs">
            <Button
              variant="inline-link"
              iconName={tableOpen ? "caret-down-filled" : "caret-right-filled"}
              onClick={() => setTableOpen(!tableOpen)}
            >
              Values ({item.rows.length} rows)
            </Button>
            {tableOpen && (
              <>
                <Table
                  variant="embedded"
                  trackBy="rowIndex"
                  items={tableItems}
                  empty="No rows"
                  columnDefinitions={[
                    ...instrumentColumns.map((col) => ({
                      id: `instrument-${col}`,
                      header: col,
                      cell: columnCell(col),
                    })),
                    ...item.conditionColumns.map((col) => ({
                      id: `cond-${col}`,
                      header: col,
                      cell: columnCell(col),
                    })),
                    ...item.valueColumns.map((col) => ({
                      id: `val-${col}`,
                      header: col,
                      cell: columnCell(col),
                    })),
                    {
                      id: "__rule",
                      header: "Rule",
                      cell: (tr: TableRow) => (
                        <SpaceBetween direction="horizontal" size="xxs">
                          <Button
                            variant="inline-link"
                            onClick={() => setRuleRow(ruleRow === tr.rowIndex ? null : tr.rowIndex)}
                          >
                            {ruleRow === tr.rowIndex ? "Hide" : "Show"}
                          </Button>
                          {canEdit && (
                            <Button
                              variant="icon"
                              iconName="close"
                              ariaLabel="Remove"
                              onClick={() => removeRow(tr.rowIndex)}
                            />
                          )}
                        </SpaceBetween>
                      ),
                    },
                  ]}
                />
                {ruleRow !== null && item.rows[ruleRow] && (
                  <RulePreview item={item} row={item.rows[ruleRow]} />
                )}
                {canEdit && (
                  <Button variant="inline-link" iconName="add-plus" onClick={addRow}>
                    + Add Row
                  </Button>
                )}
              </>
            )}
          </SpaceBetween>
        )}

        {/* Delete button */}
        {canEdit && (
          <Box>
            <Button iconName="remove" onClick={() => onRemove(index)}>
              Delete Test Concept
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </ExpandableSection>
  )
}

function ColPicker({
  available,
  onPick,
  label,
}: {
  available: string[]
  onPick: (col: string) => void
  label: string
}) {
  const [value, setValue] = useState("")

  const pick = (col: string) => {
    const trimmed = col.trim()
    if (!trimmed) return
    onPick(trimmed)
    setValue("")
  }

  return (
    <Autosuggest
      value={value}
      ariaLabel={label}
      placeholder={label}
      options={available.map((c) => ({ value: c }))}
      enteredTextLabel={(v) => `Use "${v}"`}
      onChange={({ detail }) => {
        setValue(detail.value)
        // Preserve prior behavior: selecting a known column adds it immediately.
        if (available.includes(detail.value)) pick(detail.value)
      }}
      onSelect={({ detail }) => pick(detail.value)}
      onKeyDown={({ detail }) => {
        if (detail.key === "Enter") pick(value)
      }}
    />
  )
}

function RulePreview({ item, row }: { item: TestConceptGroup; row: Record<string, string> }) {
  const reason = noRuleReason(item)
  if (reason) {
    return (
      <Box color="text-status-inactive" fontSize="body-s">
        No data quality rule is generated because {reason}.
      </Box>
    )
  }
  // Find all rows with the same condition values
  const condKey = item.conditionColumns.map((c) => row[c] || "").join("\x00")
  const matchingRows = item.rows.filter(
    (r) => item.conditionColumns.map((c) => r[c] || "").join("\x00") === condKey,
  )

  // RISK: This preview duplicates rule_generator_test_concept.py logic.
  // If the backend generator changes, this preview will be out of sync.
  return (
    <CollapsibleRule>
      <span className="text-gray-500">CustomSql &quot;SELECT * FROM primary WHERE </span>
      {matchingRows.map((r, ri) => {
        const isThis = r === row
        const clause = [...item.conditionColumns, ...item.valueColumns]
          .map((c) => `${c} = '${r[c]?.trim() || ""}'`)
          .join(" AND ")
        return (
          <span key={ri}>
            {ri > 0 && <span className="text-gray-500"> OR </span>}
            <span className={isThis ? "font-bold text-sky-700" : "text-gray-500"}>({clause})</span>
          </span>
        )
      })}
      <span className="text-gray-500">&quot;</span>
    </CollapsibleRule>
  )
}
